import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { OnItemSelect } from '@adsk/alloy-react-dropdown';

import {
  ASSIGNEE_PICKER_RECENTS,
  RECENTS_DEFAULT_MAX_LENGTH,
  RECENTS_DEFAULT_MIN_TO_SHOW,
} from '../consts';
import { useTranslation } from '../i18n';
import {
  GroupedItem,
  ItemWithGroup,
  OnIsOpenChange,
  RecentRecord,
} from '../types';
import { useRecentsPersistence } from './useRecentsPersistence';

export type UseAssigneeRecentsProps = {
  disabledRecents: boolean;
  fieldId?: string;
  baseGroupedItems: GroupedItem[];
  selectedGroup: string;
  isLoading: boolean;
  onItemSelect?: OnItemSelect<ItemWithGroup>;
  onIsOpenChange?: OnIsOpenChange;
  isSearchActive: boolean;
};

export type UseAssigneeRecentsReturn = {
  groupedItems: GroupedItem[];
  onItemSelect: OnItemSelect<ItemWithGroup>;
  onIsOpenChange: OnIsOpenChange;
};

export function itemToRecentRecord(item: ItemWithGroup): RecentRecord {
  return {
    id: item.value,
    type: item.group,
  };
}

/**
 * Manages the "Recents" section prepended to the AssigneePicker dropdown.
 *
 * Isolates recents concerns from the core data-fetching logic in useAssigneePicker.
 */
export const useAssigneeRecents = ({
  disabledRecents,
  fieldId,
  baseGroupedItems,
  selectedGroup,
  isLoading,
  onItemSelect: onItemSelectProp,
  onIsOpenChange: onIsOpenChangeProp,
  isSearchActive,
}: UseAssigneeRecentsProps): UseAssigneeRecentsReturn => {
  const { t } = useTranslation();

  const { recents, addToRecents, refreshRecents } =
    useRecentsPersistence(fieldId);

  // Tracks whether the dropdown is currently open so the fallback effect below
  // does not bypass the freeze mechanism.
  const isDropdownOpenRef = useRef(false);

  // null = not yet initialized; the fallback effect below populates it when
  // recents first loads from localStorage. This distinguishes "not yet loaded"
  // from "loaded but empty" to avoid breaking the snapshot freeze (only update recents when open state changes)
  const [openRecents, setOpenRecents] = useState<RecentRecord[] | null>(null);

  // Sync openRecents when recents first loads from localStorage. Handles the
  // initial mount and controlled-isOpen scenarios where onIsOpenChange may
  // not fire before the dropdown renders. Uses a functional update to check
  // current state (null = never initialized) without capturing it in the closure.
  // Skips when the dropdown is open to avoid bypassing the freeze: if the user
  // builds their very first recents inside a controlled-open picker, we must not
  // surface them until the next open cycle.
  useEffect(() => {
    if (recents.length > 0 && !isDropdownOpenRef.current) {
      setOpenRecents((prev) => (prev === null ? recents : prev));
    }
  }, [recents]);

  const groupedItems = useMemo(() => {
    if (
      disabledRecents ||
      isLoading ||
      !openRecents ||
      openRecents.length === 0
    ) {
      return baseGroupedItems;
    }

    const allItems = baseGroupedItems.flatMap((g) => g.items);
    // Recent items correctly resolved (without stale items)
    const resolvedRecents = openRecents
      .map((r) =>
        allItems.find((item) => item.value === r.id && item.group === r.type),
      )
      .filter((item): item is ItemWithGroup => item !== undefined);

    if (resolvedRecents.length < RECENTS_DEFAULT_MIN_TO_SHOW) {
      return baseGroupedItems;
    }

    // Recent items for the currently selected group / pill
    const groupRecents =
      selectedGroup === 'all'
        ? resolvedRecents
        : resolvedRecents.filter((item) => item.group === selectedGroup);

    // Enforce minimum number of recent items to show
    // Shallow spread is safe: ItemWithGroup fields are all primitives (strings, numbers, boolean flags).
    // If the type grows to include object-typed fields, structured-clone would be needed.
    let recentItems =
      groupRecents.length >= RECENTS_DEFAULT_MIN_TO_SHOW
        ? groupRecents.map((item) => ({ ...item, fromRecents: true }))
        : [];

    // Enforce max items display cap on recents if not searching
    if (!isSearchActive) {
      recentItems = recentItems.slice(0, RECENTS_DEFAULT_MAX_LENGTH);
    }

    // items may be [] for sparse categories — the dropdown suppresses empty groups
    // visually, but the slot at index 0 must stay to keep base-group indices stable.
    const recentsGroup: GroupedItem = {
      label: t('RECENTS'),
      items: recentItems,
      group: ASSIGNEE_PICKER_RECENTS,
      pinned: true,
    };

    return [recentsGroup, ...baseGroupedItems];
  }, [
    disabledRecents,
    isLoading,
    openRecents,
    baseGroupedItems,
    isSearchActive,
    selectedGroup,
    t,
  ]);

  const onItemSelect: OnItemSelect<ItemWithGroup> = useCallback(
    (item, context) => {
      if (!disabledRecents && context.action === 'select') {
        addToRecents(itemToRecentRecord(item));
      }

      onItemSelectProp?.(item, context);
    },
    [disabledRecents, addToRecents, onItemSelectProp],
  );

  const onIsOpenChange: OnIsOpenChange = useCallback(
    ({ isOpen }) => {
      isDropdownOpenRef.current = !!isOpen;

      if (isOpen) {
        // Read directly from localStorage so multiple instances sharing the same
        // storage key always snapshot the latest recents, not stale React state.
        setOpenRecents(refreshRecents());
      }

      onIsOpenChangeProp?.({ isOpen });
    },
    [onIsOpenChangeProp, refreshRecents],
  );

  return {
    groupedItems,
    onItemSelect,
    onIsOpenChange,
  };
};
